import { useState } from 'react';
import { ThumbsUp, ThumbsDown, Sparkles, AlertCircle, Clock3, ExternalLink, FileText, Radio, CalendarDays, Kanban } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { ContextoRadar } from '@/components/radar/ContextoRadar';
import { Modal } from '@/components/ui/Modal';

interface FeedItem {
  id: number;
  tipo: 'funil';
  licitacaoId: number;
  titulo: string;
  orgao: string | null;
  uf: string | null;
  municipio: string | null;
  modalidade: string | null;
  valorEstimado: number | null;
  prazoAbertura: string | null;
  link?: string | null;
  score: number;
  recomendado: boolean;
  motivo: string;
  explicacao: string;
  decisaoRevisor: 'pendente' | 'aceito' | 'descartado';
  decididoEm: string | null;
  createdAt: string;
  fatoresFavoraveis?: string[];
  fatoresContrarios?: string[];
  analise?: { completa: boolean; pendencias: string[] };
  feedbackDescarte?: string | null;
}

interface FeedProatividadeProps {
  itens: FeedItem[];
  modo: 'tecnico' | 'simples';
  empresaId?: number;
  /** Destino do botão "Ir para gestão" na confirmação de acompanhamento. */
  linkGestao?: string;
  /** Destino do botão "Ver calendário" na confirmação de acompanhamento. */
  linkCalendario?: string;
  onDecidir?: (itemId: number, decisao: 'aceito' | 'descartado', motivoDescarte?: string) => void;
  /** Abre uma conversa já vinculada à oportunidade escolhida no Feed. */
  onAskOpportunity?: (item: Pick<FeedItem, 'licitacaoId' | 'titulo'>, prompt?: string) => void;
  isLoading?: boolean;
}

const EMPRESA_PRE_DISPUTA_ACTIONS = [
  { tipo: 'pedido_esclarecimento', label: 'Pedir esclarecimento', icon: FileText },
] as const;

function formatCurrency(value: number | null): string {
  if (value === null || value === undefined) return 'Não informado';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

function formatDate(value: string | null): string {
  if (!value) return 'Não informado';
  return new Date(value).toLocaleDateString('pt-BR');
}

function timeRemaining(value: string | null): string {
  if (!value) return 'Prazo não informado';
  const days = Math.ceil((new Date(value).getTime() - Date.now()) / 86400000);
  if (days < 0) return 'Prazo encerrado';
  if (days === 0) return 'Encerra hoje';
  return `${days} dia(s) restante(s)`;
}

export function FeedProatividade({ itens, modo, empresaId, linkGestao, linkCalendario, onDecidir, onAskOpportunity, isLoading }: FeedProatividadeProps) {
  const hrefGestao = linkGestao ?? (empresaId ? `/gestao-licitacoes?empresa=${empresaId}` : '/gestao-licitacoes');
  const hrefCalendario = linkCalendario ?? (empresaId ? `/calendario?empresa=${empresaId}` : '/calendario');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [discardId, setDiscardId] = useState<number | null>(null);
  const [discardReason, setDiscardReason] = useState('objeto');
  const [acompanhamentoPendente, setAcompanhamentoPendente] = useState<FeedItem | null>(null);
  const [acompanhamentoConfirmado, setAcompanhamentoConfirmado] = useState<FeedItem | null>(null);

  const confirmarAcompanhamento = () => {
    if (!acompanhamentoPendente || !onDecidir) return;
    onDecidir(acompanhamentoPendente.id, 'aceito');
    setAcompanhamentoConfirmado(acompanhamentoPendente);
    setAcompanhamentoPendente(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="p-6 animate-pulse">
            <div className="h-5 bg-ds-surface2 rounded w-2/3 mb-4" />
            <div className="h-4 bg-ds-surface2 rounded w-1/2 mb-2" />
            <div className="h-4 bg-ds-surface2 rounded w-1/3" />
          </Card>
        ))}
      </div>
    );
  }

  if (itens.length === 0) {
    return (
      <Card className="p-8 text-center">
        <AlertCircle className="h-10 w-10 text-ds-text-secondary mx-auto mb-3" />
        <h3 className="text-lg font-medium text-ds-text-primary mb-1">
          Nenhuma oportunidade no momento
        </h3>
        <p className="text-sm text-ds-text-secondary">
          {modo === 'simples'
            ? 'Assim que encontrarmos editais compatíveis com o seu perfil, eles aparecerão aqui.'
            : 'Nenhum item de funil ou alerta encontrado para os filtros selecionados.'}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {itens.map((item) => {
        const isExpanded = expandedId === item.id;
        const isDecidido = item.decisaoRevisor !== 'pendente';

        return (
          <Card key={item.id} className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <h3 className="text-base font-semibold text-ds-text-primary">
                    {item.titulo}
                  </h3>
                  {item.recomendado ? (
                    <Badge variant="default" className="gap-1 border-ds-brand/20 bg-ds-brand-subtle/50 text-ds-brand">
                      <Sparkles className="h-3 w-3" />
                      {modo === 'simples' ? 'Boa chance' : 'Recomendado'}
                    </Badge>
                  ) : (
                    <Badge variant="default" className="gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {modo === 'simples' ? 'Aderência baixa' : 'Não recomendado'}
                    </Badge>
                  )}
                </div>

                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-ds-text-secondary mb-3">
                  {item.orgao && <span>{item.orgao}</span>}
                  {item.uf && <span>{item.uf}{item.municipio ? ` — ${item.municipio}` : ''}</span>}
                  {item.modalidade && <span>{item.modalidade}</span>}
                  <span>Abertura: {formatDate(item.prazoAbertura)}</span>
                  <span className="inline-flex items-center gap-1"><Clock3 className="h-3.5 w-3.5" />{timeRemaining(item.prazoAbertura)}</span>
                  <span>Valor: {formatCurrency(item.valorEstimado)}</span>
                </div>

                <p className="mb-3 text-xs text-ds-text-secondary">Fonte: PNCP • acompanhamento manual pelo portal oficial</p>

                {modo === 'simples' && (
                  <section className="mb-4 rounded-lg border border-ds-brand/25 bg-ds-brand-subtle/30 p-3" aria-label={`Radar da oportunidade ${item.titulo}`}>
                    <div className="flex items-start gap-2">
                      <Radio className="mt-0.5 h-4 w-4 shrink-0 text-ds-brand" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-ds-text-primary">Radar detectou uma oportunidade compatível</p>
                        <p className="mt-1 text-sm text-ds-text-secondary">{item.motivo}</p>
                        <p className="mt-1 text-xs text-ds-text-secondary"><strong className="text-ds-text-primary">Prazo ou risco:</strong> {timeRemaining(item.prazoAbertura)}. Revise o edital antes de confirmar a participação.</p>
                        {isDecidido && <p className="mt-1 text-xs text-ds-text-secondary">Decidido em {formatDate(item.decididoEm)}.</p>}
                        <div className="mt-3 flex flex-wrap gap-2">
                          {onDecidir && (
                            <>
                              <Button
                                size="sm"
                                variant="success"
                                disabled={isDecidido && item.decisaoRevisor === 'aceito'}
                                onClick={() => setAcompanhamentoPendente(item)}
                                className="gap-2"
                              >
                                <ThumbsUp className="h-4 w-4" />
                                Quero acompanhar
                              </Button>
                              <Button
                                size="sm"
                                variant="danger"
                                disabled={isDecidido && item.decisaoRevisor === 'descartado'}
                                onClick={() => setDiscardId(item.id)}
                                className="gap-2"
                              >
                                <ThumbsDown className="h-4 w-4" />
                                Não é para mim
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              if (onAskOpportunity) {
                                onAskOpportunity(
                                  { licitacaoId: item.licitacaoId, titulo: item.titulo },
                                  `Explique o contexto desta licitação: "${item.titulo}". Mostre por que ela apareceu para a empresa, os riscos, as evidências disponíveis e o que deve ser conferido antes de decidir.`
                                );
                                return;
                              }
                              setExpandedId(item.id);
                            }}
                          >
                            Ver contexto
                          </Button>
                          {onAskOpportunity && <Button size="sm" variant="secondary" className="border-violet-700 bg-violet-700 text-white shadow-sm hover:bg-violet-800 focus:ring-violet-700 dark:border-violet-700 dark:bg-violet-700 dark:text-white dark:hover:bg-violet-800 dark:focus:ring-violet-300" onClick={() => onAskOpportunity({ licitacaoId: item.licitacaoId, titulo: item.titulo })}>
                            <Sparkles className="h-4 w-4" /> Perguntar à IA
                          </Button>}
                        </div>
                      </div>
                    </div>
                  </section>
                )}

                {modo !== 'simples' && <p className="text-sm text-ds-text-primary leading-relaxed">{item.motivo}</p>}
                {modo === 'simples' && <p className="mt-2 text-xs text-ds-text-secondary"><strong>Importante:</strong> boa aderência não significa habilitação confirmada. Confira o edital e seus documentos.</p>}

                {item.analise && !item.analise.completa && <div className="mt-3 rounded-lg border border-ds-warning/30 bg-ds-warning/10 p-3 text-sm" role="status">Análise parcial: faltam {item.analise.pendencias.join(', ')}.</div>}

                {isExpanded && modo === 'tecnico' && (
                  <div className="mt-3 p-3 rounded-lg bg-ds-surface2 text-sm text-ds-text-secondary">
                    <strong className="text-ds-text-primary">Explicação simples:</strong>
                    <p className="mt-1">{item.explicacao}</p>
                    {!!item.fatoresFavoraveis?.length && <p className="mt-2"><strong>Fatores favoráveis:</strong> {item.fatoresFavoraveis.join(', ')}</p>}
                    {!!item.fatoresContrarios?.length && <p className="mt-1"><strong>Pontos de atenção:</strong> {item.fatoresContrarios.join(', ')}</p>}
                  </div>
                )}

                {isExpanded && modo === 'simples' && <ContextoRadar explicacao={item.explicacao} fatoresFavoraveis={item.fatoresFavoraveis} fatoresContrarios={item.fatoresContrarios} pendencias={item.analise?.pendencias} prazo={timeRemaining(item.prazoAbertura)} />}
              </div>

              <div className="flex shrink-0 flex-col items-stretch gap-2">
                {modo === 'tecnico' && (
                  <div className="text-lg font-bold text-ds-text-primary">
                    {item.score}%
                  </div>
                )}

                {modo === 'tecnico' && <button
                  onClick={() => setExpandedId(isExpanded ? null : item.id)}
                  className="inline-flex items-center justify-center rounded-lg border border-ds-border bg-ds-surface px-3 py-1.5 text-xs font-medium text-ds-text-primary transition-colors hover:bg-ds-surface2 focus:outline-none focus:ring-2 focus:ring-ds-info"
                >
                  {isExpanded ? 'Menos detalhes' : 'Mais detalhes'}
                </button>}
                {modo === 'simples' && (
                  <a
                    href={item.link || `/portal/oportunidades/${item.licitacaoId}`}
                    target={item.link ? '_blank' : undefined}
                    rel={item.link ? 'noopener noreferrer' : undefined}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-ds-brand-solid px-3 py-1.5 text-xs font-medium text-ds-on-brand transition-opacity hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-ds-brand"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Abrir oportunidade
                  </a>
                )}
              </div>
            </div>

            {onDecidir && modo !== 'simples' && (
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-ds-border">
                <Button
                  size="sm"
                  variant={item.decisaoRevisor === 'aceito' ? 'primary' : 'secondary'}
                  disabled={isDecidido && item.decisaoRevisor === 'aceito'}
                  onClick={() => onDecidir(item.id, 'aceito')}
                  className="gap-2"
                >
                  <ThumbsUp className="h-4 w-4" />
                  Aceitar
                </Button>
                <Button
                  size="sm"
                  variant={item.decisaoRevisor === 'descartado' ? 'danger' : 'secondary'}
                  disabled={isDecidido && item.decisaoRevisor === 'descartado'}
                  onClick={() => setDiscardId(item.id)}
                  className="gap-2"
                >
                  <ThumbsDown className="h-4 w-4" />
                  Descartar
                </Button>
                {isDecidido && (
                  <span className="text-xs text-ds-text-secondary ml-auto">
                    Decidido em {formatDate(item.decididoEm)}
                  </span>
                )}
              </div>
            )}
            {modo === 'simples' && empresaId && (
              <div className="mt-3 rounded-lg border border-ds-brand/20 bg-ds-brand-subtle/30 p-3">
                <div className="mb-2">
                  <h4 className="text-sm font-semibold text-ds-text-primary">Atuar no edital</h4>
                  <p className="mt-0.5 text-xs text-ds-text-secondary">Use o Redator Ninja para preparar uma peça para esta licitação.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {EMPRESA_PRE_DISPUTA_ACTIONS.map(({ tipo, label, icon: Icon }) => (
                    <a
                      key={tipo}
                      href={`/agentes-ia/redator-ninja?empresaId=${empresaId}&licitacaoId=${item.licitacaoId}&tipo=${tipo}`}
                      className="inline-flex items-center justify-center rounded-lg border border-ds-brand/40 bg-ds-brand-subtle/40 px-3 py-1.5 text-sm font-medium text-ds-brand transition-colors hover:bg-ds-brand-subtle/60 focus:outline-none focus:ring-2 focus:ring-ds-brand"
                    >
                      <Icon className="mr-2 h-4 w-4" />
                      {label}
                    </a>
                  ))}
                </div>
              </div>
            )}
            {onDecidir && discardId === item.id && <div className="mt-3 rounded-lg bg-ds-surface2 p-3"><label className="text-sm"><span className="block mb-1">Por que não é para você? (opcional)</span><select aria-label="Motivo do descarte" value={discardReason} onChange={(event) => setDiscardReason(event.target.value)} className="rounded-lg border border-ds-border bg-ds-surface px-3 py-2"><option value="objeto">Objeto não combina</option><option value="localidade">Localidade</option><option value="prazo">Prazo</option><option value="capacidade">Capacidade</option><option value="documentacao">Documentação</option><option value="outro">Outro</option></select></label><div className="mt-2 flex gap-2"><Button size="sm" variant="danger" onClick={() => { onDecidir(item.id, 'descartado', discardReason); setDiscardId(null); }}>Confirmar descarte</Button><Button size="sm" variant="secondary" onClick={() => setDiscardId(null)}>Cancelar</Button></div></div>}
          </Card>
        );
      })}
      <Modal isOpen={Boolean(acompanhamentoPendente)} onClose={() => setAcompanhamentoPendente(null)} title="Acompanhar esta licitação?">
        <p className="text-sm text-ds-text-secondary">Ao confirmar, esta licitação entrará na sua Gestão da Licitação. Os prazos relevantes também poderão ser acompanhados no Calendário.</p>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => setAcompanhamentoPendente(null)}>Agora não</Button>
          <Button size="sm" variant="primary" onClick={confirmarAcompanhamento}><ThumbsUp className="mr-2 h-4 w-4" />Confirmar acompanhamento</Button>
        </div>
      </Modal>
      <Modal isOpen={Boolean(acompanhamentoConfirmado)} onClose={() => setAcompanhamentoConfirmado(null)} title="Licitação em acompanhamento">
        <div className="space-y-3">
          <div className="rounded-lg border border-ds-success/30 bg-ds-success/10 p-3 text-sm text-ds-text-primary" role="status">
            <p className="font-semibold">Tudo certo: esta licitação agora está em gestão.</p>
            <p className="mt-1 text-ds-text-secondary">Acompanhe as próximas etapas na Gestão da Licitação e consulte os prazos no Calendário.</p>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button size="sm" variant="secondary" onClick={() => setAcompanhamentoConfirmado(null)}>Continuar no feed</Button>
            <a href={hrefCalendario} className="inline-flex min-h-9 items-center justify-center gap-2 rounded-lg border border-ds-border bg-ds-surface px-3 py-1.5 text-sm font-medium text-ds-text-primary hover:bg-ds-surface2"><CalendarDays className="h-4 w-4" />Ver calendário</a>
            <a href={hrefGestao} className="inline-flex min-h-9 items-center justify-center gap-2 rounded-lg bg-ds-brand-solid px-3 py-1.5 text-sm font-medium text-ds-on-brand hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-ds-brand"><Kanban className="h-4 w-4" />Ir para gestão</a>
          </div>
        </div>
      </Modal>
    </div>
  );
}
